import { createContext, FC, useState, useEffect, useMemo } from 'react';
import { default as React } from 'react';
import { gql, useQuery, useLazyQuery } from '@apollo/client';
import { useApi } from '@backstage/core-plugin-api';
import jwt from 'jsonwebtoken';
import { devhubAuthApiRef } from '../../apis';
import { ManagerAndDirectReportsAppsQuery } from '../components/apps/ManagerAndDirectReportsAppsQuery';

/**
 * Active Directory group name for DevHub admin permissions
 */
export const DEVHUB_ADMIN_GROUP = '[CHQ -- DevHub]';

interface UserContextData {
  login: string;
  firstName?: string;
  hasRoleOnApp: (
    userData: UserRoleData | undefined,
    roleName: string,
  ) => boolean;
  canEdit: (userData: UserRoleData | undefined) => boolean;
  hasGroupMembership: (
    adData: GroupMembershipData | undefined,
    groupName: string,
  ) => boolean;
  isExecutive: boolean;
  isAdmin: boolean;
  isAdminLoading: boolean;
  checkDirectReports: () => Promise<void>;
}

export type UserRoleData = {
  firstName: string;
  lastName: string;
  login: string;
  resources: { roles: Role[] };
};

export type GroupMembershipData = {
  login: string;
  memberOf: Group[];
};

type Group = {
  name: string;
};

type Role = {
  name: string;
  application: Application;
};

type Application = {
  name: string;
  applicationId: number;
};

type Props = {
  children: React.ReactNode;
};

/**
 * UserContext provides user identity and permissions data
 */
export const UserContext = createContext<UserContextData>({
  login: '',
  hasRoleOnApp: () => false,
  canEdit: () => false,
  hasGroupMembership: () => false,
  isExecutive: false,
  isAdmin: false,
  isAdminLoading: true,
  checkDirectReports: async () => {},
});

const GetGroupMembershipQuery = gql`
  query GetGroupMembership($login: String!, $filter: GroupListFilter) {
    identity {
      user(login: $login) {
        memberOf(filter: $filter) {
          name
        }
        login
      }
    }
  }
`;

const GetUserRolesInApplication = gql`
  query UserRolesInApplication($login: String!, $applicationId: Int!) {
    identity {
      user(login: $login) {
        firstName
        lastName
        email
        resources {
          roles(filter: { applicationId: $applicationId }) {
            name
          }
        }
      }
    }
  }
`;

/**
 * A hook that gets roles and information about a user
 * @param applicationId - The id of the application
 * @param login - The login of the user
 */
export const useUserRoles = (
  applicationId: number | string,
  login: string,
): {
  loading: boolean;
  error?: Error;
  value?: UserRoleData;
} => {
  const { loading, error, data } = useQuery(GetUserRolesInApplication, {
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
    variables: {
      login: login,
      applicationId:
        typeof applicationId === 'string'
          ? parseInt(applicationId, 10)
          : applicationId,
    },
  });
  return {
    loading: loading,
    error: error,
    value: data?.identity?.user,
  };
};

/**
 * Hook that checks if a user is a member of an AD group
 * @param login the login of the user logged in
 * @param adGroups the AD groups search string to filter for
 */
export const useGroupMembership = (
  login: string,
  adGroups: string[],
): {
  loading: boolean;
  error?: Error;
  value?: GroupMembershipData;
} => {
  const {
    loading: adLoading,
    error: adError,
    data: adData,
  } = useQuery(GetGroupMembershipQuery, {
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
    variables: {
      login: login,
      filter: {
        names: adGroups,
      },
    },
  });
  return {
    loading: adLoading,
    error: adError,
    value: adData?.identity?.user,
  };
};

/**
 * UserProvider gets roles and information about a user
 * @param props - The props of this context.
 * @param props.children - The children of this context.
 */
export const UserProvider: FC<Props> = (props: Props) => {
  const [login, setLogin] = useState<string>('');
  const [isExecutive, setIsExecutive] = useState<boolean>(false);
  const authApi = useApi(devhubAuthApiRef);

  // Check admin group membership
  const { loading: adminGroupLoading, value: adminGroupData } =
    useGroupMembership(login || '', [DEVHUB_ADMIN_GROUP]);

  const isAdmin = useMemo(() => {
    if (!adminGroupData || adminGroupLoading) return false;
    return (
      adminGroupData.memberOf?.some(
        (group: Group) => group.name === DEVHUB_ADMIN_GROUP,
      ) ?? false
    );
  }, [adminGroupData, adminGroupLoading]);

  const [fetchDirectReports, { data: directReportsData }] = useLazyQuery(
    ManagerAndDirectReportsAppsQuery,
    {
      errorPolicy: 'all',
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
    },
  );

  // Function to check if user has direct reports
  const checkDirectReports = async (): Promise<void> => {
    if (login) {
      fetchDirectReports({
        variables: {
          login: login,
          pagination: {
            limit: 10, // We only need to know if there are any, not all of them
            offset: 0,
          },
        },
      });
    }
  };

  useEffect(() => {
    (async function getIdentity() {
      const token = await authApi.getIdToken();
      if (token) {
        const decodedToken: jwt.JwtPayload = jwt.decode(
          token ?? '',
        ) as jwt.JwtPayload;
        // External users do not have SAM account name so use email in place
        const userLogin =
          decodedToken?.onpremisessamaccountname ?? decodedToken?.email;
        setLogin(userLogin);
      }
    })();
  }, [authApi]);

  useEffect(() => {
    if (directReportsData) {
      const applications =
        directReportsData?.identity?.user?.resources?.applicationPortfolio
          ?.applications || [];
      // If the user has any applications in their portfolio, consider them an executive
      setIsExecutive(applications.length > 0);
    }
  }, [directReportsData]);

  useEffect(() => {
    if (login) {
      checkDirectReports();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [login]);

  const hasGroupMembership = (
    adData: GroupMembershipData | undefined,
    groupName: string,
  ): boolean => {
    if (!adData) {
      return false;
    }

    return adData?.memberOf?.some((group: Group) => group?.name === groupName);
  };

  const canEdit = (userData: UserRoleData | undefined): boolean => {
    const allowedRoles = ['owner', 'assistant_owner', 'approver'];

    return (
      userData?.resources?.roles?.some((role: Role) =>
        allowedRoles.includes(role?.name),
      ) ?? false
    );
  };

  const hasRoleOnApp = (
    userData: UserRoleData | undefined,
    roleName: string,
  ): boolean => {
    if (!userData) {
      return false;
    }
    return userData?.resources?.roles?.some(
      (role: Role) => role?.name === roleName,
    );
  };

  return (
    <UserContext.Provider
      value={{
        login,
        hasRoleOnApp,
        canEdit,
        hasGroupMembership,
        isExecutive,
        isAdmin,
        isAdminLoading: adminGroupLoading,
        checkDirectReports,
      }}
    >
      {props.children}
    </UserContext.Provider>
  );
};

export default UserProvider;
